import type { GetDelegateTokenResponse } from '@anchorbase/pillar-sdk';
import {
    resolveGatewayTokenizationCardBrand,
    resolveGatewayTokenizationPaymentToken,
    tokenizeCardWithGatewayDelegateToken,
    type GatewayDelegateTokenizationResponse,
} from './ipg/card-tokenization';

export type CardBrand =
    | 'Visa'
    | 'Mastercard'
    | 'AmEx'
    | 'Discover'
    | 'Diners Club'
    | 'JCB'
    | 'Unknown';

export type CardFormData = {
    cardNumber: string;
    expiryDate: string;
    cvv: string;
    cardholderName: string;
};

type CardBrandConfiguration = {
    brand: CardBrand;
    regex: RegExp;
    mask: string;
    securityCodeLength: number;
    panLengths: ReadonlyArray<number>;
};

type TokenizationRequestParams = {
    cardData: CardFormData;
    delegateToken: GetDelegateTokenResponse;
};

const CardBrandConfigurations: ReadonlyArray<CardBrandConfiguration> = [
    {
        brand: 'Visa',
        regex: /^4/,
        mask: '0000 0000 0000 0000',
        securityCodeLength: 3,
        panLengths: [16],
    },
    {
        brand: 'Mastercard',
        regex: /^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[0-1]|2720)/,
        mask: '0000 0000 0000 0000',
        securityCodeLength: 3,
        panLengths: [16],
    },
    {
        brand: 'AmEx',
        regex: /^3[47]/,
        mask: '0000 000000 00000',
        securityCodeLength: 4,
        panLengths: [15],
    },
    {
        brand: 'JCB',
        regex: /^(?:30(?:8[89]|9[0-4]|9[6-9])|310[0-2]|31(?:1[2-9]|20|5[89])|33(?:3[7-9]|4\d)|35(?:2[89]|[3-8]\d))/,
        mask: '0000 0000 0000 0000 000',
        securityCodeLength: 3,
        panLengths: [16, 17, 18, 19],
    },
    {
        brand: 'Diners Club',
        regex: /^36/,
        mask: '0000 000000 000000',
        securityCodeLength: 3,
        panLengths: [14, 15, 16],
    },
    {
        brand: 'Diners Club',
        regex: /^(?:30[0-5]|3[89])/,
        mask: '0000 0000 0000 0000 000',
        securityCodeLength: 3,
        panLengths: [16, 17, 18, 19],
    },
    {
        brand: 'Discover',
        regex: /^6(?:011|4[4-9]|5[0-8])/,
        mask: '0000 0000 0000 0000 000',
        securityCodeLength: 3,
        panLengths: [16, 17, 18, 19],
    },
];

export function detectCardBrandConfiguration(
    cardNumber: string
): CardBrandConfiguration | null {
    const normalizedPan = cardNumber.replace(/\D/g, '');
    return (
        CardBrandConfigurations.find((configuration) =>
            configuration.regex.test(normalizedPan)
        ) ?? null
    );
}

export function formatCardNumber(value: string): string {
    const normalizedDigits = value.replace(/\D/g, '');
    const detectedConfiguration = detectCardBrandConfiguration(normalizedDigits);
    const mask = detectedConfiguration?.mask ?? '0000 0000 0000 0000';
    const maskCharacters = mask.split('');
    let digitIndex = 0;
    let formattedValue = '';

    for (const maskCharacter of maskCharacters) {
        if (digitIndex >= normalizedDigits.length) {
            break;
        }
        if (maskCharacter === '0') {
            formattedValue += normalizedDigits[digitIndex] ?? '';
            digitIndex += 1;
            continue;
        }
        formattedValue += maskCharacter;
    }

    return formattedValue;
}

export function formatExpiryDate(value: string): string {
    const normalizedDigits = value.replace(/\D/g, '').slice(0, 4);
    if (normalizedDigits.length <= 2) {
        return normalizedDigits;
    }
    return `${normalizedDigits.slice(0, 2)}/${normalizedDigits.slice(2)}`;
}

export function resolveCardBrand(cardNumber: string): CardBrand {
    return detectCardBrandConfiguration(cardNumber)?.brand ?? 'Unknown';
}

export function validateCardFormData(cardData: CardFormData): string | null {
    const normalizedPan = cardData.cardNumber.replace(/\D/g, '');
    const detectedConfiguration = detectCardBrandConfiguration(cardData.cardNumber);
    const acceptedPanLengths = detectedConfiguration?.panLengths ?? [15, 16];
    if (!acceptedPanLengths.includes(normalizedPan.length)) {
        return 'Enter a valid card number.';
    }

    const expectedCvvLength = detectedConfiguration?.securityCodeLength ?? 3;
    if (cardData.cvv.length !== expectedCvvLength) {
        return 'Enter a valid security code.';
    }

    if (!/^\d{2}\/\d{2}$/.test(cardData.expiryDate)) {
        return 'Enter a valid expiration date.';
    }

    if (cardData.cardholderName.trim().length === 0) {
        return 'Enter the cardholder name.';
    }

    return null;
}

export async function tokenizeCardWithDelegateToken(
    params: TokenizationRequestParams
): Promise<GatewayDelegateTokenizationResponse> {
    return tokenizeCardWithGatewayDelegateToken(params);
}

export {
    resolveGatewayTokenizationCardBrand,
    resolveGatewayTokenizationPaymentToken,
};
